import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, ArrowLeft, Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'checking' | 'registered' | 'not_found'
  const debounceRef = useRef(null);

  const handleEmailChange = (val) => {
    setEmail(val);
    setEmailStatus(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // validasi format email sederhana dulu
    if (!val || !/\S+@\S+\.\S+/.test(val)) return;
    setEmailStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/auth/check-email?email=${encodeURIComponent(val)}`);
        setEmailStatus(data.registered ? 'registered' : 'not_found');
      } catch {
        setEmailStatus(null);
      }
    }, 600);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (emailStatus === 'not_found') {
      setError('Email ini tidak terdaftar. Pastikan email yang Anda masukkan benar atau daftar akun baru.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(email)}`);
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Terjadi kesalahan. Coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #1E3A8A 0%, #1D4ED8 60%, #3B82F6 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #FCD34D 0%, transparent 50%), radial-gradient(circle at 80% 20%, #FFFFFF 0%, transparent 50%)' }} />

        <div className="relative flex items-center gap-4">
          <img src="/logo-white.png" alt="RencanaGuru" className="h-16 w-16 object-contain drop-shadow-lg" />
          <div>
            <div className="font-bold text-2xl text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-400">.com</span>
            </div>
            <div className="text-blue-300 text-xs font-medium">Platform AI untuk Guru Indonesia</div>
          </div>
        </div>

        <div className="relative flex flex-col gap-6">
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Lupa Password?<br />Tenang, Kami Bantu!
            </h2>
            <p className="text-blue-200 text-lg leading-relaxed">
              Masukkan email yang terdaftar dan kami akan kirimkan link untuk membuat password baru.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {[
              '🔒 Link reset berlaku selama 1 jam',
              '📧 Cek folder Spam jika tidak muncul di Inbox',
              '✅ Password baru langsung bisa digunakan',
            ].map(item => (
              <div key={item} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3.5">
                <span className="text-sm text-blue-100">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-blue-300 text-xs">© 2026 RencanaGuru.com</div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12"
        style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #F0FDF4 100%)' }}>
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center gap-2 mb-8">
            <img src="/logo.png" alt="RencanaGuru" className="h-16 w-16 object-contain" />
            <span className="font-bold text-xl text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-500">.com</span>
            </span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-2xl shadow-lg p-8">

            {sent ? (
              /* Success state */
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-blue-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Email Terkirim!
                </h1>
                <p className="text-slate-500 text-sm mb-2">
                  Kami telah mengirimkan link reset password ke:
                </p>
                <p className="font-semibold text-blue-700 text-sm mb-6 bg-blue-50 rounded-xl px-4 py-2">
                  {email}
                </p>
                <p className="text-slate-400 text-xs mb-8 leading-relaxed">
                  Cek inbox atau folder Spam Anda. Link berlaku selama <strong>1 jam</strong>.
                  Jika tidak ada email, periksa kembali alamat email Anda.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => { setSent(false); setEmail(''); }}
                    className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
                  >
                    Kirim Ulang Email
                  </button>
                  <Link
                    to="/login"
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-semibold text-sm"
                    style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
                  >
                    <ArrowLeft size={16} /> Kembali ke Login
                  </Link>
                </div>
              </div>
            ) : (
              /* Form state */
              <>
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                    <Mail size={24} className="text-blue-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-blue-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Lupa Password
                  </h1>
                  <p className="text-slate-500 text-sm">
                    Masukkan email akun Anda dan kami akan kirimkan link reset password.
                  </p>
                </div>

                {error && (
                  <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">⚠</span>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="email" className="text-slate-700 font-semibold text-sm">Email</Label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        required
                        className={`rounded-xl bg-white text-base h-11 pr-11 transition-colors ${
                          emailStatus === 'not_found'
                            ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                            : emailStatus === 'registered'
                            ? 'border-green-400 focus:border-green-500 focus:ring-green-200'
                            : 'border-blue-100 focus:border-blue-500 focus:ring-blue-500'
                        }`}
                        placeholder="nama@email.com"
                        autoFocus
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {emailStatus === 'checking' && <Loader2 size={16} className="animate-spin text-slate-400" />}
                        {emailStatus === 'registered' && <CheckCircle size={16} className="text-green-500" />}
                        {emailStatus === 'not_found' && <XCircle size={16} className="text-red-500" />}
                      </div>
                    </div>
                    {emailStatus === 'registered' && (
                      <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle size={12} /> Email terdaftar.
                      </p>
                    )}
                    {emailStatus === 'not_found' && (
                      <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                        <XCircle size={12} /> Email ini belum terdaftar.{' '}
                        <Link to="/register" className="underline font-bold">Daftar sekarang</Link>
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || emailStatus === 'not_found' || emailStatus === 'checking'}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-base shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                    style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Mengirim...
                      </>
                    ) : (
                      <>
                        Kirim Link Reset Password
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-5 pt-5 border-t border-blue-100 text-center">
                  <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-semibold">
                    <ArrowLeft size={15} /> Kembali ke Login
                  </Link>
                </div>
              </>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            © 2026 RencanaGuru.com · Platform AI untuk Guru Indonesia
          </p>
        </div>
      </div>
    </div>
  );
};
